import React, { useEffect, useMemo } from "react";
import { Column, Text } from "@amzn/meridian";
import { useAppDispatch, useAppSelector } from "src/store/store";
import QuestionDisplayCard from "./questionDisplayCard";
import ObjectGroupCard from "./ObjectGroupCard";
import { useParams } from "react-router-dom";
import { setSelectedInspectionTemplate } from "src/store/inspectionTemplateSlice";
import { Question } from "src/data/types";
import { getObjectInstanceDetails } from "src/components/pages/inspection/utils";

type QuestionsListProps = {
  onQuestionSelect?: (question: Question) => void;
  onObjectGroupSelect?: (objectType: string, questions: Question[]) => void;
  processedQuestions?: {
    groups: { [objectType: string]: Question[] };
    individuals: Question[];
  };
};

const QuestionsList = ({ 
  onQuestionSelect, 
  onObjectGroupSelect,
  processedQuestions 
}: QuestionsListProps) => {
  const { inspectionTemplates, selectedInspectionTemplate } = useAppSelector(
    (state) => state.inspectionTemplate
  );
  const { inspectionResults } = useAppSelector((state) => state.inspectionResults);
  const { templateId } = useParams();
  const dispatch = useAppDispatch();
  
  useEffect(() => {
    if (!selectedInspectionTemplate && templateId) {
      const template = inspectionTemplates.find(
        (temp) => temp.templateId === templateId
      );
      if (template) dispatch(setSelectedInspectionTemplate(template));
    }
  }, [selectedInspectionTemplate, templateId, inspectionTemplates]);

  // Use processedQuestions if provided, otherwise fall back to original logic
  const questions = useMemo(() => {
    if (processedQuestions) {
      return processedQuestions;
    }
    // Fallback: treat all questions as individuals
    return {
      groups: {},
      individuals: selectedInspectionTemplate?.questions || []
    };
  }, [processedQuestions, selectedInspectionTemplate]);

  // Separate unanswered and answered for individuals
  const unansweredIndividuals = questions.individuals.filter(
    (q) => !inspectionResults.find(r => r.questionId === q.questionId)
  );
  
  const answeredIndividuals = questions.individuals.filter(
    (q) => inspectionResults.find(r => r.questionId === q.questionId)
  );

  // Separate unanswered and answered for object groups
  const unansweredGroups: { [key: string]: Question[] } = {};
  const answeredGroups: { [key: string]: Question[] } = {};
  
  Object.entries(questions.groups).forEach(([objectType, groupQuestions]) => {
    const allAnswered = groupQuestions.every(q => 
      inspectionResults.find(r => r.questionId === q.questionId)
    );
    
    if (allAnswered) {
      answeredGroups[objectType] = groupQuestions;
    } else {
      unansweredGroups[objectType] = groupQuestions;
    }
  });

  const hasUnanswered = Object.keys(unansweredGroups).length > 0 || unansweredIndividuals.length > 0;
  const hasAnswered = Object.keys(answeredGroups).length > 0 || answeredIndividuals.length > 0;

  return (
    <Column spacing="400">
      {/* Unanswered Section */}
      {hasUnanswered && (
        <Column spacing="300">
          {/* Unanswered Object Groups */}
          {Object.entries(unansweredGroups).map(([objectType, groupQuestions]) => {
            const instanceDetails = getObjectInstanceDetails(objectType, groupQuestions, inspectionResults as any);
            return (
              <ObjectGroupCard
                key={objectType}
                objectType={objectType}
                questions={groupQuestions}
                completedInstanceCount={instanceDetails.instanceCount}
                onSelect={onObjectGroupSelect || (() => {})}
              />
            );
          })}
          
          {/* Unanswered Individual Questions */}
          {unansweredIndividuals.map((question) => {
            const originalIndex = selectedInspectionTemplate?.questions?.indexOf(question) ?? 0;
            return (
              <QuestionDisplayCard
                key={question.questionId}
                question={question}
                questionOrder={originalIndex + 1}
                onSelect={onQuestionSelect}
                result={undefined}
              />
            );
          })}
        </Column>
      )}
      
      {/* Answered Section */}
      {hasAnswered && (
        <Column spacing="200">
          {hasUnanswered && (
            <Column spacingInset="300 none none">
              <Text type="b200" color="secondary">
                Completed Questions
              </Text>
            </Column>
          )}
          <Column spacing="300">
            {/* Answered Object Groups */}
            {Object.entries(answeredGroups).map(([objectType, groupQuestions]) => {
              const instanceDetails = getObjectInstanceDetails(objectType, groupQuestions, inspectionResults as any);
              return (
                <ObjectGroupCard
                  key={objectType}
                  objectType={objectType}
                  questions={groupQuestions}
                  completedInstanceCount={instanceDetails.instanceCount}
                  onSelect={onObjectGroupSelect || (() => {})}
                />
              );
            })}
            
            {/* Answered Individual Questions */}
            {answeredIndividuals.map((question) => {
              const originalIndex = selectedInspectionTemplate?.questions?.indexOf(question) ?? 0;
              const result = inspectionResults.find(r => r.questionId === question.questionId);
              return (
                <QuestionDisplayCard
                  key={question.questionId}
                  question={question}
                  questionOrder={originalIndex + 1}
                  onSelect={onQuestionSelect}
                  result={result}
                />
              );
            })}
          </Column>
        </Column>
      )}
    </Column>
  );
};

export default QuestionsList;
